import { useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { Link, createFileRoute } from "@tanstack/react-router";

import {
  OverageEditDialog,
  type OveragePayload,
} from "@/components/admin/features/overage-edit-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminContext } from "@/lib/admin-context";

/**
 * Admin → Features × Plans matrix.
 *
 * Port of `src/app/admin/features/features-matrix-client.tsx` (legacy NextJS, ~565 LOC) per D21.
 *
 * V1.1 scope (P5): matrix view + scope/category filter + cell access selector + limit input +
 * upgrade-to selector + advanced PUT body fields via the Overage editor dialog
 * (overageEnabled / overageUnitPrice / overageMaxUnits / batchSize / batchPrice).
 *
 * Backend currently returns an empty `features` array (TODO W7 — port catalog.ts to
 * @pontimed/feature-gating). UI handles this by rendering an empty-state hint instead of
 * crashing.
 *
 * Endpoints (live):
 *   - GET /api/admin/features
 *   - PUT /api/admin/features
 */

type FeatureScope = "user" | "provider" | "facility" | "business";

interface FeatureDefinition {
  key: string;
  scope: FeatureScope;
  category: string;
  labelRo: string;
  labelEn: string;
  type: string;
  status: string;
  defaultLimit?: number;
  notes?: string;
}

interface Plan {
  code: string;
  targetType: string;
  name: Record<string, string>;
  isActive: boolean;
  visibility: string;
}

interface CellConfig {
  access: string;
  limit: number | null;
  upgradeTo: string | null;
  overageEnabled?: boolean;
  overageUnitPrice?: number | null;
  overageMaxUnits?: number | null;
  batchSize?: number | null;
  batchPrice?: number | null;
}

type Assignments = Record<string, Record<string, CellConfig>>;

const ACCESS_OPTIONS = ["hidden", "included", "limited", "soon", "upgrade", "teaser"] as const;

export const Route = createFileRoute("/_authenticated/features")({
  component: FeaturesMatrixPage,
});

function FeaturesMatrixPage() {
  const { t } = useLingui();
  const { dbTarget } = useAdminContext();
  const [features, setFeatures] = useState<FeatureDefinition[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [assignments, setAssignments] = useState<Assignments>({});
  const [loading, setLoading] = useState(true);
  const [scopeFilter, setScopeFilter] = useState<FeatureScope>("facility");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [savingCell, setSavingCell] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch
      .get<{ features: FeatureDefinition[]; plans: Plan[]; assignments: Assignments }>(
        "/api/admin/features",
      )
      .then((data) => {
        setFeatures(data.features ?? []);
        setPlans(data.plans ?? []);
        setAssignments(data.assignments ?? {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dbTarget]);

  const visibleFeatures = useMemo(
    () =>
      features.filter(
        (f) =>
          f.scope === scopeFilter && (categoryFilter === "all" || f.category === categoryFilter),
      ),
    [features, scopeFilter, categoryFilter],
  );

  const visiblePlans = useMemo(() => {
    const typeMap: Record<FeatureScope, string> = {
      user: "user",
      provider: "provider",
      facility: "facility",
      business: "business",
    };
    return plans.filter((p) => p.targetType === typeMap[scopeFilter]);
  }, [plans, scopeFilter]);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const f of features) if (f.scope === scopeFilter) s.add(f.category);
    return Array.from(s).sort();
  }, [features, scopeFilter]);

  const scopeCounts: Record<FeatureScope, number> = {
    user: 0,
    provider: 0,
    facility: 0,
    business: 0,
  };
  for (const f of features) scopeCounts[f.scope]++;

  const handleCellChange = async (
    planCode: string,
    featureKey: string,
    access: string,
    limit: number | null,
    upgradeTo: string | null,
    overageFields?: Partial<OveragePayload>,
  ): Promise<void> => {
    const cellId = `${planCode}:${featureKey}`;
    setSavingCell(cellId);
    try {
      await apiFetch.put("/api/admin/features", {
        planCode,
        featureKey,
        access,
        limit,
        upgradeTo,
        ...(overageFields ?? {}),
      });
      setAssignments((prev) => ({
        ...prev,
        [planCode]: {
          ...prev[planCode],
          [featureKey]: {
            ...(prev[planCode]?.[featureKey] ?? {}),
            access,
            limit,
            upgradeTo,
            ...(overageFields ?? {}),
          },
        },
      }));
    } finally {
      setSavingCell(null);
    }
  };

  const [overageEdit, setOverageEdit] = useState<{
    planCode: string;
    featureKey: string;
  } | null>(null);

  const handleOverageSave = async (payload: OveragePayload): Promise<void> => {
    if (!overageEdit) return;
    const cell = assignments[overageEdit.planCode]?.[overageEdit.featureKey];
    if (!cell) return;
    await handleCellChange(
      overageEdit.planCode,
      overageEdit.featureKey,
      cell.access,
      cell.limit,
      cell.upgradeTo,
      payload,
    );
    setOverageEdit(null);
  };

  const SCOPE_LABEL: Record<FeatureScope, React.ReactNode> = {
    user: <Trans>Users</Trans>,
    provider: <Trans>Providers</Trans>,
    facility: <Trans>Facilities</Trans>,
    business: <Trans>Businesses</Trans>,
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">
            <Trans>Features × Plans matrix</Trans>
          </h1>
          <p className="text-muted-foreground text-sm">
            <Trans>
              {features.length} features · {plans.length} plans · edit cells inline
            </Trans>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/features-analytics">
            <Button variant="outline" size="sm">
              <Trans>Analytics →</Trans>
            </Button>
          </Link>
          <Link to="/features-overages">
            <Button variant="outline" size="sm">
              <Trans>Overages →</Trans>
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {(["user", "provider", "facility", "business"] as const).map((s) => (
          <button
            key={s}
            onClick={() => {
              setScopeFilter(s);
              setCategoryFilter("all");
            }}
            className={`rounded-full px-4 py-1.5 text-xs font-medium transition-all ${
              scopeFilter === s
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {SCOPE_LABEL[s]}
            <span className="ml-1 opacity-60">({scopeCounts[s]})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button
          onClick={() => setCategoryFilter("all")}
          className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all ${
            categoryFilter === "all"
              ? "bg-foreground text-background"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          <Trans>All</Trans>
        </button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            className={`rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all ${
              categoryFilter === c
                ? "bg-foreground text-background"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <Card>
          <CardContent className="space-y-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-3/4" />
          </CardContent>
        </Card>
      ) : visibleFeatures.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground p-8 text-center text-sm">
            <Trans>
              No features registered for this scope. The catalog will be populated when the
              feature-gating package is wired up.
            </Trans>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-border/50 bg-muted/30 sticky top-0 border-b">
                  <th className="bg-muted/30 sticky left-0 min-w-[280px] px-3 py-2 text-left font-semibold">
                    <Trans>Feature</Trans>
                  </th>
                  {visiblePlans.map((p) => (
                    <th key={p.code} className="min-w-[140px] px-2 py-2 text-center font-semibold">
                      <div>{p.name.ro || p.name.en || p.code}</div>
                      <div className="text-muted-foreground mt-0.5 font-mono text-[0.5625rem]">
                        {p.code}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleFeatures.map((f) => (
                  <tr key={f.key} className="border-border/30 hover:bg-muted/20 border-b">
                    <td className="bg-card sticky left-0 px-3 py-2">
                      <div className="font-medium">{f.labelRo}</div>
                      <div className="text-muted-foreground mt-0.5 font-mono text-[0.5625rem]">
                        {f.key}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant="outline" className="h-4 text-[0.5625rem]">
                          {f.category}
                        </Badge>
                        <Badge
                          variant={
                            f.status === "built"
                              ? "default"
                              : f.status === "wip"
                                ? "secondary"
                                : "outline"
                          }
                          className="h-4 text-[0.5625rem]"
                        >
                          {f.status}
                        </Badge>
                        <Badge variant="outline" className="h-4 text-[0.5625rem]">
                          {f.type}
                        </Badge>
                      </div>
                    </td>
                    {visiblePlans.map((p) => {
                      const cell = assignments[p.code]?.[f.key];
                      const cellId = `${p.code}:${f.key}`;
                      const isSaving = savingCell === cellId;
                      const currentAccess = cell?.access ?? "hidden";
                      const currentLimit = cell?.limit;
                      const currentUpgradeTo = cell?.upgradeTo ?? null;
                      const canHaveUpgradeTarget =
                        currentAccess === "upgrade" ||
                        currentAccess === "soon" ||
                        currentAccess === "teaser" ||
                        (currentAccess === "limited" && (currentLimit ?? 0) === 0);

                      return (
                        <td key={p.code} className="px-1.5 py-1.5 text-center align-top">
                          <div className="flex flex-col items-center gap-1">
                            <select
                              value={currentAccess}
                              onChange={(e) =>
                                handleCellChange(
                                  p.code,
                                  f.key,
                                  e.target.value,
                                  currentLimit ?? null,
                                  currentUpgradeTo,
                                )
                              }
                              disabled={isSaving}
                              className={`w-full rounded border px-1.5 py-0.5 text-[0.6875rem] font-medium ${
                                isSaving ? "opacity-50" : ""
                              } ${
                                currentAccess === "included"
                                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                  : currentAccess === "limited"
                                    ? "border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-400"
                                    : currentAccess === "soon"
                                      ? "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                      : currentAccess === "upgrade"
                                        ? "border-purple-500/50 bg-purple-500/10 text-purple-700 dark:text-purple-400"
                                        : currentAccess === "teaser"
                                          ? "border-pink-500/50 bg-pink-500/10 text-pink-700 dark:text-pink-400"
                                          : "border-border bg-muted text-muted-foreground"
                              }`}
                            >
                              {ACCESS_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                            {currentAccess === "limited" && (
                              <>
                                <input
                                  type="number"
                                  value={currentLimit ?? 0}
                                  onChange={(e) =>
                                    handleCellChange(
                                      p.code,
                                      f.key,
                                      "limited",
                                      Number(e.target.value),
                                      currentUpgradeTo,
                                    )
                                  }
                                  disabled={isSaving}
                                  placeholder={t`limit`}
                                  className="w-full rounded border px-1.5 py-0.5 text-center text-[0.6875rem]"
                                  title={t`-1 = unlimited, 0 = disabled`}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setOverageEdit({ planCode: p.code, featureKey: f.key })
                                  }
                                  disabled={isSaving}
                                  title={t`Configure overage / batch pack`}
                                  aria-label={t`Configure overage for ${p.code} × ${f.key}`}
                                  className={`min-h-[28px] w-full rounded border px-1 py-0.5 text-center text-[0.625rem] transition-colors ${
                                    cell?.overageEnabled || (cell?.batchSize ?? 0) > 0
                                      ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                      : "border-border bg-muted text-muted-foreground hover:bg-muted/60"
                                  }`}
                                >
                                  ⚡{" "}
                                  {cell?.overageEnabled
                                    ? `€${cell.overageUnitPrice ?? 0}/u`
                                    : (cell?.batchSize ?? 0) > 0
                                      ? `+${cell?.batchSize}/€${cell?.batchPrice ?? 0}`
                                      : t`overage?`}
                                </button>
                              </>
                            )}
                            {canHaveUpgradeTarget && (
                              <select
                                value={currentUpgradeTo ?? ""}
                                onChange={(e) =>
                                  handleCellChange(
                                    p.code,
                                    f.key,
                                    currentAccess,
                                    currentLimit ?? null,
                                    e.target.value || null,
                                  )
                                }
                                disabled={isSaving}
                                className="text-muted-foreground w-full rounded border px-1.5 py-0.5 text-center text-[0.625rem]"
                              >
                                <option value="">{t`— upgrade to —`}</option>
                                {visiblePlans
                                  .filter((vp) => vp.code !== p.code)
                                  .map((vp) => (
                                    <option key={vp.code} value={vp.code}>
                                      {vp.code}
                                    </option>
                                  ))}
                              </select>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div className="text-muted-foreground text-[0.6875rem]">
        <Trans>
          Limit legend: <span className="font-mono">-1</span> = unlimited ·{" "}
          <span className="font-mono">0</span> = disabled but visible ·{" "}
          <span className="font-mono">N</span> = monthly cap · <span className="font-mono">⚡</span>{" "}
          = configure overage / batch pack
        </Trans>
      </div>

      {overageEdit && (
        <OverageEditDialog
          open
          planCode={overageEdit.planCode}
          featureKey={overageEdit.featureKey}
          cell={assignments[overageEdit.planCode]?.[overageEdit.featureKey]}
          onSave={handleOverageSave}
          onClose={() => setOverageEdit(null)}
        />
      )}
    </div>
  );
}
